import { AuthService } from './authService';
import type {
  LegalEntity,
  Department,
  ProcurementItemLookup,
  CreateProcurementOrderRequest,
  CreateProcurementOrderResponse,
  UpsertLegalEntityRequest,
  UpsertDepartmentRequest,
  ProcurementItemAdmin,
  UpsertProcurementItemRequest,
  ProcurementItemBalanceAdmin,
  UpsertProcurementItemBalanceRequest,
} from '../types/procurement';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class ProcurementService {
  private readonly baseUrl = `${API_BASE_URL}/procurement`;

  async getLegalEntities(): Promise<LegalEntity[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/legal-entities`);
    if (!response.ok) {
      throw new Error(`Failed to load legal entities: ${response.status}`);
    }
    return response.json();
  }

  async getDepartments(legalEntityNo: string): Promise<Department[]> {
    const response = await AuthService.authenticatedGet(
      `${this.baseUrl}/legal-entities/${encodeURIComponent(legalEntityNo)}/departments`
    );
    if (!response.ok) {
      throw new Error(`Failed to load departments: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Look up a procurement item by either ProcurementItemNo or ProcurementItemBarCode.
   * Returns null when no match is found (HTTP 404).
   */
  async lookupItem(
    legalEntityNo: string,
    departmentNo: string,
    code: string
  ): Promise<ProcurementItemLookup | null> {
    const params = new URLSearchParams({ legalEntityNo, departmentNo, code });
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/items/lookup?${params}`);

    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Lookup failed: ${response.status}`);
    }
    return response.json();
  }

  async createOrderWithLines(
    request: CreateProcurementOrderRequest
  ): Promise<CreateProcurementOrderResponse> {
    const response = await AuthService.authenticatedPost(
      `${this.baseUrl}/orders/with-lines`,
      request
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Failed to create order: ${response.status}`);
    }
    return response.json();
  }

  // =======================================================================
  // Admin - LegalEntity
  // =======================================================================

  async createLegalEntity(request: UpsertLegalEntityRequest): Promise<LegalEntity> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/legal-entities`, request);
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
    return response.json();
  }

  async updateLegalEntity(legalEntityNo: string, request: UpsertLegalEntityRequest): Promise<LegalEntity> {
    const response = await AuthService.authenticatedPut(
      `${this.baseUrl}/legal-entities/${encodeURIComponent(legalEntityNo)}`,
      request
    );
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
    return response.json();
  }

  async deleteLegalEntity(legalEntityNo: string): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${this.baseUrl}/legal-entities/${encodeURIComponent(legalEntityNo)}`
    );
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
  }

  // =======================================================================
  // Admin - Department
  // =======================================================================

  async getAllDepartments(): Promise<Department[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/departments`);
    if (!response.ok) throw new Error(`Failed: ${response.status}`);
    return response.json();
  }

  async createDepartment(request: UpsertDepartmentRequest): Promise<Department> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/departments`, request);
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
    return response.json();
  }

  async updateDepartment(legalEntityNo: string, departmentNo: string, request: UpsertDepartmentRequest): Promise<Department> {
    const response = await AuthService.authenticatedPut(
      `${this.baseUrl}/legal-entities/${encodeURIComponent(legalEntityNo)}/departments/${encodeURIComponent(departmentNo)}`,
      request
    );
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
    return response.json();
  }

  async deleteDepartment(legalEntityNo: string, departmentNo: string): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${this.baseUrl}/legal-entities/${encodeURIComponent(legalEntityNo)}/departments/${encodeURIComponent(departmentNo)}`
    );
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
  }

  // =======================================================================
  // Admin - ProcurementItems
  // =======================================================================

  async getAllProcurementItems(): Promise<ProcurementItemAdmin[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/items`);
    if (!response.ok) throw new Error(`Failed: ${response.status}`);
    return response.json();
  }

  async createProcurementItem(request: UpsertProcurementItemRequest): Promise<ProcurementItemAdmin> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/items`, request);
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
    return response.json();
  }

  async updateProcurementItem(
    legalEntityNo: string, departmentNo: string, procurementItemNo: string,
    request: UpsertProcurementItemRequest
  ): Promise<ProcurementItemAdmin> {
    const response = await AuthService.authenticatedPut(
      `${this.baseUrl}/items/${encodeURIComponent(legalEntityNo)}/${encodeURIComponent(departmentNo)}/${encodeURIComponent(procurementItemNo)}`,
      request
    );
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
    return response.json();
  }

  async deleteProcurementItem(legalEntityNo: string, departmentNo: string, procurementItemNo: string): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${this.baseUrl}/items/${encodeURIComponent(legalEntityNo)}/${encodeURIComponent(departmentNo)}/${encodeURIComponent(procurementItemNo)}`
    );
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
  }

  // =======================================================================
  // Admin - ProcurementItemsBalances
  // =======================================================================

  async getAllBalances(): Promise<ProcurementItemBalanceAdmin[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/balances`);
    if (!response.ok) throw new Error(`Failed: ${response.status}`);
    return response.json();
  }

  async createBalance(request: UpsertProcurementItemBalanceRequest): Promise<ProcurementItemBalanceAdmin> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/balances`, request);
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
    return response.json();
  }

  async updateBalance(procurementItemNo: string, request: UpsertProcurementItemBalanceRequest): Promise<ProcurementItemBalanceAdmin> {
    const response = await AuthService.authenticatedPut(
      `${this.baseUrl}/balances/${encodeURIComponent(procurementItemNo)}`,
      request
    );
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
    return response.json();
  }

  async deleteBalance(procurementItemNo: string): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${this.baseUrl}/balances/${encodeURIComponent(procurementItemNo)}`
    );
    if (!response.ok) throw new Error(await response.text() || `Failed: ${response.status}`);
  }
}

export const procurementService = new ProcurementService();
